import { initI18n, useTranslation, type i18n as I18nInstance } from '@wix/wix-i18n-config';
import en from '../locales/messages_en.json';

export { useTranslation };

// The full set the product targets, mirroring the mobile/native set (see
// `ai-scheduling-assistant-mobile-platform/strings`) — the eventual coverage the
// Wix Translation Center will fill once the Babel project is registered.
export const SUPPORTED_LOCALES = [
  'ar', 'bg', 'ca', 'cs', 'da', 'de', 'el', 'en', 'es', 'fi', 'fr', 'he', 'hi', 'hr',
  'hu', 'id', 'it', 'ja', 'ko', 'lt', 'lv', 'ms', 'nl', 'no', 'pl', 'pt', 'ro', 'ru',
  'sk', 'sl', 'sv', 'th', 'tl', 'tr', 'uk', 'vi', 'zh',
] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';

// The locales we can ACTUALLY render — those with a committed `messages_<x>.json`
// bundle. `resolveLocale` only ever returns one of these, so a user whose browser
// asks for a not-yet-translated language gets English rather than a half-empty UI.
// Every supported locale now ships a bundle, so this equals SUPPORTED_LOCALES —
// but the two stay separate on purpose: a NEW target added to SUPPORTED must only
// join this list once its bundle actually lands, so the gate never promises a
// translation that isn't there. RTL locales (`ar`, `he`) render as RTL *text*;
// full RTL *layout* mirroring is still a later phase (see isRtlLocale / `dir`).
export const AVAILABLE_LOCALES = [
  'ar', 'bg', 'ca', 'cs', 'da', 'de', 'el', 'en', 'es', 'fi', 'fr', 'he', 'hi', 'hr',
  'hu', 'id', 'it', 'ja', 'ko', 'lt', 'lv', 'ms', 'nl', 'no', 'pl', 'pt', 'ro', 'ru',
  'sk', 'sl', 'sv', 'th', 'tl', 'tr', 'uk', 'vi', 'zh',
] as const;
const AVAILABLE = new Set<string>(AVAILABLE_LOCALES);

const RTL = new Set<string>(['ar', 'he']);
export const isRtlLocale = (locale: Locale): boolean => RTL.has(locale);

// Names for the settings language picker, keyed for EVERY supported locale (not
// just the available ones) so the dialog can list the not-yet-translated targets
// as "coming soon". `native` is the endonym (each language's own name) — the
// convention every OS/browser picker follows, so a user who can't read the
// current UI language still recognizes their own — and `english` is the English
// exonym shown as a subtitle for quick scanning.
export const LOCALE_NAMES: Record<string, { native: string; english: string }> = {
  ar: { native: 'العربية', english: 'Arabic' },
  bg: { native: 'Български', english: 'Bulgarian' },
  ca: { native: 'Català', english: 'Catalan' },
  cs: { native: 'Čeština', english: 'Czech' },
  da: { native: 'Dansk', english: 'Danish' },
  de: { native: 'Deutsch', english: 'German' },
  el: { native: 'Ελληνικά', english: 'Greek' },
  en: { native: 'English', english: 'English' },
  es: { native: 'Español', english: 'Spanish' },
  fi: { native: 'Suomi', english: 'Finnish' },
  fr: { native: 'Français', english: 'French' },
  he: { native: 'עברית', english: 'Hebrew' },
  hi: { native: 'हिन्दी', english: 'Hindi' },
  hr: { native: 'Hrvatski', english: 'Croatian' },
  hu: { native: 'Magyar', english: 'Hungarian' },
  id: { native: 'Bahasa Indonesia', english: 'Indonesian' },
  it: { native: 'Italiano', english: 'Italian' },
  ja: { native: '日本語', english: 'Japanese' },
  ko: { native: '한국어', english: 'Korean' },
  lt: { native: 'Lietuvių', english: 'Lithuanian' },
  lv: { native: 'Latviešu', english: 'Latvian' },
  ms: { native: 'Bahasa Melayu', english: 'Malay' },
  nl: { native: 'Nederlands', english: 'Dutch' },
  no: { native: 'Norsk', english: 'Norwegian' },
  pl: { native: 'Polski', english: 'Polish' },
  pt: { native: 'Português', english: 'Portuguese' },
  ro: { native: 'Română', english: 'Romanian' },
  ru: { native: 'Русский', english: 'Russian' },
  sk: { native: 'Slovenčina', english: 'Slovak' },
  sl: { native: 'Slovenščina', english: 'Slovenian' },
  sv: { native: 'Svenska', english: 'Swedish' },
  th: { native: 'ไทย', english: 'Thai' },
  tl: { native: 'Tagalog', english: 'Filipino' },
  tr: { native: 'Türkçe', english: 'Turkish' },
  uk: { native: 'Українська', english: 'Ukrainian' },
  vi: { native: 'Tiếng Việt', english: 'Vietnamese' },
  zh: { native: '中文', english: 'Chinese' },
};

// Back-compat endonym-only map (the picker uses LOCALE_NAMES directly now).
export const LOCALE_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(LOCALE_NAMES).map(([code, n]) => [code, n.native]),
);

// Normalize a raw locale, Site Properties language code, or `Accept-Language` value to a
// locale we can serve, or `null` when there is nothing usable in it.
// Accept-Language arrives regioned and weighted ("he-IL,he;q=0.9,en;q=0.8"); we
// take the first tag's base language and reject it unless a bundle for it actually
// exists — so shipping the plumbing ahead of the translations is a true no-op for
// every user until the matching bundle is added to AVAILABLE_LOCALES.
//
// The `null` matters: "this source has no answer" and "this source says English" are
// DIFFERENT, and collapsing them is what made a failed Site Properties read silently
// reset a Czech user to English on every reload. Only `resolveInitialLocale` below
// decides when to give up and default.
export function matchLocale(input?: string | null): Locale | null {
  if (!input) return null;
  const firstTag = input.split(',')[0]?.trim().split(';')[0]?.trim() ?? '';
  const base = firstTag.split('-')[0]?.toLowerCase() ?? '';
  return AVAILABLE.has(base) ? (base as Locale) : null;
}

export function resolveLocale(input?: string | null): Locale {
  return matchLocale(input) ?? DEFAULT_LOCALE;
}

// The ONE precedence rule for "which language is this browser in", shared by the SSR
// seed and the client effect so the two cannot drift:
//
//   1. `stored`         — an explicit Settings pick in THIS browser. Always wins.
//   2. `siteLanguage`   — the account's Site Properties `language`, written by whichever
//                         surface the user last picked on. Gives a fresh browser the
//                         language they chose on their phone.
//   3. `browserLanguage`— Accept-Language / `navigator.language`.
//   4. English.
//
// Every source may be absent, and an absent source is SKIPPED rather than treated as
// English — a Site Properties read that 401s/404s/throws must never downgrade a user
// who has already chosen a language.
export function resolveInitialLocale(sources: {
  stored?: Locale | null;
  siteLanguage?: string | null;
  browserLanguage?: string | null;
}): Locale {
  return (
    sources.stored ??
    matchLocale(sources.siteLanguage) ??
    matchLocale(sources.browserLanguage) ??
    DEFAULT_LOCALE
  );
}

// The user's explicit Settings pick, persisted per browser — step 1 above, and the
// reason a chosen language survives a reload, a new tab and a signed-out moment. The
// native apps resolve the same way (`Loc.resolve()` reads its `symphony.language`
// override before the OS locale and never reads Site Properties back), so this is the
// web half of one cross-surface rule rather than a web-only convenience.
//
// Written to BOTH `localStorage` and a cookie. `localStorage` is the client-side source
// of truth; the cookie exists so the SERVER can read the pick in `getInitialProps` and
// SSR directly in that language — without it every load paints English and then flips,
// which for `he`/`ar` is a full LTR→RTL relayout. One year, `SameSite=Lax` (functional,
// first-party — sent on normal navigations).
//
// The key deliberately keeps its pre-#6438 name so a browser that still holds a pick
// from before that PR dropped this layer gets its language back on the next load.
export const LOCALE_STORAGE_KEY = 'orion-web.locale';
const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export function getStoredLocale(): Locale | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (raw && AVAILABLE.has(raw)) return raw as Locale;
  } catch {
    // Fall through to the cookie when localStorage is unavailable (private mode).
  }
  return typeof document === 'undefined' ? null : localeFromCookieHeader(document.cookie);
}

export function setStoredLocale(locale: Locale): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Private-mode / storage-disabled: rely on the cookie below to persist.
  }
  try {
    document.cookie = `${LOCALE_STORAGE_KEY}=${locale}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    // Cookies disabled too: the choice just won't survive a reload.
  }
}

// Parse the persisted pick out of a raw `Cookie:` header (SSR) or `document.cookie`
// (client). Validated against AVAILABLE so a stale/removed code falls back to the next
// source rather than seeding a bundle-less locale. Exported for `getInitialProps`.
export function localeFromCookieHeader(cookieHeader?: string | null): Locale | null {
  return localeCookieValue(cookieHeader, LOCALE_STORAGE_KEY);
}

function localeCookieValue(cookieHeader: string | null | undefined, key: string): Locale | null {
  if (!cookieHeader) return null;
  const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = cookieHeader.match(new RegExp(`(?:^|;\\s*)${escaped}=([^;]+)`));
  if (!match?.[1]) return null;
  // Base-normalize (`zh-CN` → `zh`) so an externally-set regioned value still resolves —
  // mirrors matchLocale and the _document inline stamp. Our own writer only ever stores a
  // validated base code, so this is just robustness.
  let raw = '';
  try {
    raw = decodeURIComponent(match[1]);
  } catch {
    raw = match[1];
  }
  return matchLocale(raw);
}

// The account's Site Properties `language`, cached per browser so SSR can seed the first
// paint with it. This is NOT the Settings pick and must never be stored as one: a pick is
// authoritative and sticky (it would stop a language later chosen on the phone from ever
// propagating here), while this cache is a best-effort mirror of the LAST fetched account
// language — the client refetches Site Properties on every load and rewrites it, so an
// account-side change still lands (one load later at worst). Without it, a user whose
// language comes from the account rather than a local pick or the browser gets an English
// SSR paint and a client-side flip — the "flash of English" — on every full page load.
// A NEW key takes the product name (see the localStorage-prefix note in CLAUDE.md).
export const SITE_LOCALE_COOKIE = 'symphony-site-locale';

export function siteLocaleFromCookieHeader(cookieHeader?: string | null): Locale | null {
  return localeCookieValue(cookieHeader, SITE_LOCALE_COOKIE);
}

export function getCachedSiteLocale(): Locale | null {
  return typeof document === 'undefined' ? null : siteLocaleFromCookieHeader(document.cookie);
}

// Cache the freshly-fetched Site Properties language for the next SSR. A `null` fetch (no
// tenant, 401 before the session settles, a blip) leaves the cache alone — an absent source
// must never downgrade — while a real answer we can't render clears it, so a stale cached
// language can't outlive the account's actual one.
export function setCachedSiteLocale(languageCode: string | null): void {
  if (typeof document === 'undefined' || languageCode === null) return;
  const matched = matchLocale(languageCode);
  try {
    document.cookie = matched
      ? `${SITE_LOCALE_COOKIE}=${matched}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax`
      : `${SITE_LOCALE_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    // Cookies disabled: SSR just won't see the account language; the client effect still applies it.
  }
}

// Lazy-load a locale's message bundle. English is bundled (instant first paint +
// i18next fallback); every other locale dynamic-imports its own JSON. The webpack
// context only picks up files actually present under `locales/`, so a locale with
// no bundle rejects and is caught by `activateLocale`. Exported so SSR
// (`_app.getInitialProps`) can await the first-paint locale's bundle and ship it
// in `__NEXT_DATA__` — see `getI18n` seeding below.
export const loadLocaleMessages = async (locale: Locale): Promise<Record<string, string>> => {
  if (locale === DEFAULT_LOCALE) return en as Record<string, string>;
  const mod = await import(`../locales/messages_${locale}.json`);
  return (mod.default ?? mod) as Record<string, string>;
};

// Initialize the instance already in `locale` with `messages` resident. We
// deliberately do NOT pass `asyncMessagesLoader`: when `resources` are provided,
// i18next won't consult a backend for on-demand languages unless
// `partialBundledLanguages: true`, and @wix/wix-i18n-config doesn't expose that
// flag — so the backend never fires and other locales would silently stay on the
// seed. Instead, later switches add their bundle explicitly via `activateLocale`.
//
// `initI18n({ locale, messages })` registers `messages` under `locale` only and
// sets `fallbackLng: 'en'`; when we seed a non-English locale we add the English
// bundle too so any (theoretically) missing key still resolves via the fallback.
const createInstance = (locale: Locale, messages: Record<string, string>): I18nInstance => {
  const instance = initI18n({
    locale,
    messages,
    useSuspense: false,
    // Subscribe useTranslation to these i18next events so components actually
    // re-render when `activateLocale` switches language after mount. react-i18next
    // defaults this to 'languageChanged', but wix-i18n-config forwards our value
    // verbatim (undefined would overwrite the default → no subscription → the UI
    // would keep the first-render language forever). 'loaded' covers the bundle
    // arriving right after the switch.
    bindI18n: 'languageChanged loaded',
  });
  if (locale !== DEFAULT_LOCALE && !instance.hasResourceBundle(DEFAULT_LOCALE, 'translation')) {
    instance.addResourceBundle(DEFAULT_LOCALE, 'translation', en as Record<string, string>, true, true);
  }
  return instance;
};

// Load (once) and switch to a locale. English is already resident, so it's a
// no-op switch; any other locale gets its bundle added on demand, then
// `changeLanguage` fires the re-render. A missing/broken bundle is swallowed so
// the UI simply stays on its current language.
//
// `isStale` guards against out-of-order resolution: a locale switch awaits an
// async bundle import, so with a quick A→B→A (or overlapping seed/refine) the
// slower import could otherwise `changeLanguage` last and win over the user's
// latest choice. The caller passes a checker that turns true once a newer
// activation supersedes this one; we bail before mutating the instance.
export async function activateLocale(
  instance: I18nInstance,
  locale: Locale,
  isStale?: () => boolean,
): Promise<void> {
  try {
    if (locale !== DEFAULT_LOCALE && !instance.hasResourceBundle(locale, 'translation')) {
      const messages = await loadLocaleMessages(locale);
      if (isStale?.()) return;
      instance.addResourceBundle(locale, 'translation', messages, true, true);
    }
    if (isStale?.()) return;
    if (instance.language !== locale) await instance.changeLanguage(locale);
  } catch {
    // No bundle for this locale (or load failed) → stay on the current language.
  }
}

// One instance per browser tab (client). Switching language is the caller's job
// via `activateLocale` from an effect — never here, so this stays side-effect-free
// and safe to call during render.
let clientInstance: I18nInstance | null = null;

// Resolve the seed into a (locale, messages) pair the instance can init with
// synchronously. We only start in a non-English locale when its bundle is
// actually in hand (`seed.messages`, shipped via `__NEXT_DATA__`); with no
// messages we fall back to English so we never render a bundle-less locale
// (which would show raw keys). English always has its statically-imported bundle.
const resolveSeed = (seed?: I18nSeed): { locale: Locale; messages: Record<string, string> } => {
  if (seed && seed.locale !== DEFAULT_LOCALE && seed.messages) {
    return { locale: seed.locale, messages: seed.messages };
  }
  return { locale: DEFAULT_LOCALE, messages: en as Record<string, string> };
};

// A first-paint seed: the SSR-resolved locale plus its bundle, both carried to
// the client in `__NEXT_DATA__` so the instance can be built in that language
// synchronously — no post-hydration language swap, no English→target flicker.
export type I18nSeed = { locale: Locale; messages: Record<string, string> | null };

export function getI18n(seed?: I18nSeed): I18nInstance {
  const { locale, messages } = resolveSeed(seed);
  // Server: a fresh instance per request. A module-level singleton would be
  // shared across all concurrent SSR requests, so one request's language could
  // bleed into another's render — build an isolated instance instead.
  if (typeof window === 'undefined') return createInstance(locale, messages);
  // Client: reuse the tab's instance, seeded on first creation. Later renders
  // ignore the seed and return the live instance (whose language `activateLocale`
  // owns), so a client-side navigation can't reset the user's chosen language.
  if (!clientInstance) clientInstance = createInstance(locale, messages);
  return clientInstance;
}
